import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import type { PressableProps, StyleProp, ViewStyle } from 'react-native';

import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

type FacebookButtonProps = PressableProps & {
  loading?: boolean;
  style?: StyleProp<ViewStyle>;
  title: string;
};

type SocialButtonProps = FacebookButtonProps & {
  logo: 'facebook' | 'google';
  variant: 'facebook' | 'google';
};

function SocialAuthButton({
  disabled,
  logo,
  loading = false,
  style,
  title,
  variant,
  ...props
}: SocialButtonProps) {
  const isDisabled = disabled || loading;

  return (
    <Pressable
      accessibilityRole="button"
      disabled={isDisabled}
      style={({ pressed }) => [
        styles.button,
        variant === 'facebook' ? styles.facebookButton : styles.googleButton,
        pressed && !isDisabled && variant === 'facebook'
          ? styles.facebookButtonPressed
          : null,
        pressed && !isDisabled && variant === 'google'
          ? styles.googleButtonPressed
          : null,
        isDisabled ? styles.disabled : null,
        style,
      ]}
      {...props}
    >
      <View
        style={[
          styles.logoMark,
          logo === 'facebook' ? styles.facebookLogoMark : styles.googleLogoMark,
        ]}
      >
        <Text
          style={[
            logo === 'facebook'
              ? styles.facebookLogoText
              : styles.googleLogoText,
          ]}
        >
          {logo === 'facebook' ? 'f' : 'G'}
        </Text>
      </View>
      {loading ? (
        <ActivityIndicator
          color={variant === 'facebook' ? colors.surface : colors.text}
        />
      ) : (
        <Text
          numberOfLines={1}
          style={[
            styles.text,
            variant === 'facebook' ? styles.facebookText : styles.googleText,
          ]}
        >
          {title}
        </Text>
      )}
      <View style={styles.trailingSpacer} />
    </Pressable>
  );
}

export function FacebookButton(props: FacebookButtonProps) {
  return <SocialAuthButton logo="facebook" variant="facebook" {...props} />;
}

export function GoogleButton(props: FacebookButtonProps) {
  return <SocialAuthButton logo="google" variant="google" {...props} />;
}

const styles = StyleSheet.create({
  button: {
    alignItems: 'center',
    borderRadius: 6,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'center',
    minHeight: 48,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  facebookButton: {
    backgroundColor: '#1877f2',
    borderColor: '#1877f2',
  },
  facebookButtonPressed: {
    backgroundColor: '#166fe5',
    borderColor: '#166fe5',
  },
  googleButton: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
  },
  googleButtonPressed: {
    backgroundColor: colors.surfaceTint,
  },
  disabled: {
    opacity: 0.6,
  },
  logoMark: {
    alignItems: 'center',
    height: 24,
    overflow: 'hidden',
    width: 24,
  },
  facebookLogoMark: {
    backgroundColor: colors.surface,
    borderRadius: 3,
    justifyContent: 'flex-end',
  },
  facebookLogoText: {
    color: '#1877f2',
    fontFamily: 'Arial',
    fontSize: 27,
    fontWeight: '900',
    lineHeight: 27,
    marginBottom: -5,
  },
  googleLogoMark: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  googleLogoText: {
    color: '#4285f4',
    fontFamily: 'Arial',
    fontSize: 22,
    fontWeight: '900',
    lineHeight: 24,
  },
  text: {
    flex: 1,
    fontSize: 16,
    fontWeight: '800',
    textAlign: 'center',
  },
  facebookText: {
    color: colors.surface,
  },
  googleText: {
    color: colors.text,
  },
  trailingSpacer: {
    width: 24,
  },
});
